import React from 'react';
import { Info, TrendingUp, TrendingDown, Minus } from 'lucide-react';
import type { CohortMetrics, ValueScorecard as ValueScorecardData } from '../../types';

/**
 * Assisted orders next to the rest of the store's.
 *
 * Two decisions worth knowing before editing this:
 *
 * The five comparison measures carry different units — euros, a count, three shares — so they are
 * NOT plotted against a shared axis. Each row is scaled to its own pair, which is small multiples
 * in table form; putting AOV and a percentage on one scale would be the dual-axis mistake.
 *
 * Every figure here is observational, not causal. Shoppers who open an assistant already intend to
 * buy more, so a positive delta is consistent with the assistant creating value AND with it merely
 * attracting shoppers who would have spent anyway. The caveat is rendered, not tucked into a
 * tooltip, because a merchant reading these numbers is the person most likely to over-read them.
 */

interface ValueScorecardProps {
  data: ValueScorecardData;
  currency?: string;
  locale?: string;
}

type Direction = 'higher-is-better' | 'lower-is-better';

interface ComparisonRow {
  label: string;
  assisted: number;
  unassisted: number;
  delta: number;
  direction: Direction;
  format: (value: number) => string;
  note?: string;
}

export function ValueScorecard({ data, currency = 'EUR', locale = 'en-GB' }: ValueScorecardProps) {
  const money = (value: number) =>
    new Intl.NumberFormat(locale, { style: 'currency', currency, maximumFractionDigits: 0 }).format(
      value
    );
  const share = (value: number) => `${(value * 100).toFixed(1)}%`;
  const decimal = (value: number) => value.toFixed(2);
  const count = (value: number) => new Intl.NumberFormat(locale).format(value);

  const rows: ComparisonRow[] = [
    {
      label: 'Average order value',
      assisted: data.assisted.avgOrderValue,
      unassisted: data.unassisted.avgOrderValue,
      delta: data.delta.avgOrderValue,
      direction: 'higher-is-better',
      format: money
    },
    {
      label: 'Items per order',
      assisted: data.assisted.itemsPerOrder,
      unassisted: data.unassisted.itemsPerOrder,
      delta: data.delta.itemsPerOrder,
      direction: 'higher-is-better',
      format: decimal
    },
    {
      label: 'Orders using a coupon',
      assisted: data.assisted.discountedShare,
      unassisted: data.unassisted.discountedShare,
      delta: data.delta.discountedShare,
      direction: 'lower-is-better',
      format: share,
      note: 'Lower means selling without discounting.'
    },
    {
      label: 'Returning customers',
      assisted: data.assisted.repeatShare,
      unassisted: data.unassisted.repeatShare,
      delta: data.delta.repeatShare,
      direction: 'higher-is-better',
      format: share,
      note: data.historyFrom
        ? `Only counts history since ${new Date(data.historyFrom).toLocaleDateString(locale)}.`
        : 'No order history on record yet.'
    },
    {
      label: 'Returned or cancelled',
      assisted: data.assisted.returnRate,
      unassisted: data.unassisted.returnRate,
      delta: data.delta.returnRate,
      direction: 'lower-is-better',
      format: share,
      note: 'Lower is evidence the recommendations were right.'
    }
  ];

  const hasOrders = data.assisted.orders > 0 || data.unassisted.orders > 0;

  return (
    <div className="value-scorecard dark:bg-surface bg-white rounded-xl border dark:border-border-color border-gray-200 p-6">
      <style>{`
        /* Validated with the dataviz palette checker: light #6366F1/#F59E0B and dark
           #6366F1/#D97706 both pass lightness, chroma, CVD separation and contrast. Dark gets its
           own amber step rather than an automatic flip. */
        .value-scorecard { --c-assisted: #6366F1; --c-unassisted: #F59E0B; }
        .dark .value-scorecard { --c-unassisted: #D97706; }
      `}</style>

      <header className="mb-5">
        <div className="flex items-baseline justify-between gap-4 flex-wrap">
          <h2 className="text-lg font-semibold dark:text-text-primary text-gray-900">
            Assistant value
          </h2>
          <span className="text-xs dark:text-text-secondary text-gray-500">
            {new Date(data.from).toLocaleDateString(locale)} –{' '}
            {new Date(data.to).toLocaleDateString(locale)}
          </span>
        </div>
        <p className="mt-2 text-xs dark:text-text-secondary text-gray-500 flex items-start gap-1.5">
          <Info className="w-3.5 h-3.5 mt-px shrink-0" aria-hidden="true" />
          <span>
            Observational, not causal: shoppers who open the assistant already intend to buy more.
            These figures show a difference, not proof the assistant caused it.
          </span>
        </p>
      </header>

      <ContributionTiles data={data} money={money} share={share} count={count} />
      <ReachRow data={data} share={share} count={count} />
      <HoldoutPanel data={data} share={share} count={count} />

      <section aria-labelledby="cohort-comparison" className="mt-6">
        <div className="flex items-center justify-between gap-4 flex-wrap mb-3">
          <h3
            id="cohort-comparison"
            className="text-sm font-medium dark:text-text-primary text-gray-900"
          >
            Assisted orders vs the rest of the store
          </h3>
          <Legend />
        </div>

        {hasOrders ? (
          <ul className="space-y-4">
            {rows.map(row => (
              <ComparisonBar key={row.label} row={row} />
            ))}
          </ul>
        ) : (
          <p className="text-sm dark:text-text-secondary text-gray-500 py-6 text-center">
            No orders recorded in this period yet.
          </p>
        )}
      </section>
    </div>
  );
}

/** Two series, so a legend is always present — identity is never carried by colour alone. */
function Legend() {
  return (
    <ul className="flex items-center gap-4 text-xs dark:text-text-secondary text-gray-500">
      <li className="flex items-center gap-1.5">
        <span
          className="w-2.5 h-2.5 rounded-sm shrink-0"
          style={{ background: 'var(--c-assisted)' }}
          aria-hidden="true"
        />
        Assisted
      </li>
      <li className="flex items-center gap-1.5">
        <span
          className="w-2.5 h-2.5 rounded-sm shrink-0"
          style={{ background: 'var(--c-unassisted)' }}
          aria-hidden="true"
        />
        Everyone else
      </li>
    </ul>
  );
}

/**
 * One measure, scaled to its own pair.
 *
 * The bar is a comparison aid; the numbers are the record, and they are always visible — which is
 * also what discharges the amber's sub-3:1 contrast against the light surface.
 */
const ComparisonBar: React.FC<{ row: ComparisonRow }> = ({ row }) => {
  const scale = Math.max(row.assisted, row.unassisted, Number.EPSILON);
  const width = (value: number) => `${Math.max((value / scale) * 100, value > 0 ? 1.5 : 0)}%`;

  return (
    <li>
      <div className="flex items-baseline justify-between gap-3 mb-1.5">
        <span className="text-sm dark:text-text-primary text-gray-800">{row.label}</span>
        <DeltaBadge delta={row.delta} direction={row.direction} format={row.format} />
      </div>

      <div className="space-y-1">
        <Bar
          value={row.assisted}
          width={width(row.assisted)}
          color="var(--c-assisted)"
          label={row.format(row.assisted)}
          series="Assisted"
        />
        <Bar
          value={row.unassisted}
          width={width(row.unassisted)}
          color="var(--c-unassisted)"
          label={row.format(row.unassisted)}
          series="Everyone else"
        />
      </div>

      {row.note && (
        <p className="mt-1.5 text-xs dark:text-text-secondary text-gray-500">{row.note}</p>
      )}
    </li>
  );
};

function Bar({
  value,
  width,
  color,
  label,
  series
}: {
  value: number;
  width: string;
  color: string;
  label: string;
  series: string;
}) {
  return (
    <div className="flex items-center gap-2" title={`${series}: ${label}`}>
      <div className="flex-1 h-2 dark:bg-background/60 bg-gray-100 rounded-full overflow-hidden">
        <div
          className="h-full rounded-full transition-all"
          style={{ width, background: color }}
          role="img"
          aria-label={`${series}: ${label}`}
        />
      </div>
      <span className="text-xs tabular-nums dark:text-text-secondary text-gray-600 w-20 text-right shrink-0">
        {label}
      </span>
    </div>
  );
}

/**
 * Direction shipped with an icon and a word, never colour alone — and "better" is per-measure:
 * a lower return rate and a lower coupon share are both wins.
 */
function DeltaBadge({
  delta,
  direction,
  format
}: {
  delta: number;
  direction: Direction;
  format: (value: number) => string;
}) {
  if (Math.abs(delta) < Number.EPSILON) {
    return (
      <span className="text-xs dark:text-text-secondary text-gray-500 flex items-center gap-1">
        <Minus className="w-3 h-3" aria-hidden="true" />
        no difference
      </span>
    );
  }

  const better = direction === 'higher-is-better' ? delta > 0 : delta < 0;
  const Icon = delta > 0 ? TrendingUp : TrendingDown;
  const tone = better
    ? 'text-emerald-600 dark:text-emerald-400'
    : 'text-amber-700 dark:text-amber-500';

  return (
    <span className={`text-xs flex items-center gap-1 ${tone}`}>
      <Icon className="w-3 h-3" aria-hidden="true" />
      <span className="tabular-nums">
        {delta > 0 ? '+' : '−'}
        {format(Math.abs(delta))}
      </span>
      <span className="sr-only">{better ? 'better than' : 'worse than'} the rest of the store</span>
    </span>
  );
}

function ContributionTiles({
  data,
  money,
  share,
  count
}: {
  data: ValueScorecardData;
  money: (value: number) => string;
  share: (value: number) => string;
  count: (value: number) => string;
}) {
  const { contribution } = data;
  const lowConfidence = contribution.orders > 0 && contribution.tiers.tier4 / contribution.orders > 0.5;

  return (
    <section aria-label="Assistant contribution" className="grid grid-cols-2 lg:grid-cols-4 gap-3">
      <Tile
        label="Influenced revenue"
        value={money(contribution.influencedRevenue)}
        note="Only the products the assistant surfaced"
        emphasis
      />
      <Tile label="Assisted order value" value={money(contribution.revenue)} note="Full value of those orders" />
      <Tile
        label="Assisted orders"
        value={count(contribution.orders)}
        note={`${share(contribution.coverage)} of all orders · ${
          lowConfidence ? 'mostly inferred from product overlap' : 'mostly matched to a shopper'
        }`}
      />
      <ReturnOnSpendTile data={data} money={money} />
    </section>
  );
}

/**
 * The renewal number, and the one figure here that needs an input no API can supply: a storefront
 * reports an order's revenue and never the margin inside it. Absent that the tile says what is
 * missing rather than showing a number computed against revenue, which would read as profit and
 * would not be.
 */
/**
 * The one causal claim on this card, and the one that most needs protecting from impatience.
 *
 * When the sample cannot support a lift the panel shows progress toward it rather than a number.
 * A figure displayed early is a figure a merchant will quote, and on small arms the observed
 * difference is mostly noise.
 */
function HoldoutPanel({
  data,
  share,
  count
}: {
  data: ValueScorecardData;
  share: (value: number) => string;
  count: (value: number) => string;
}) {
  const { holdout } = data;
  if (!holdout.active) return null;

  const smaller = Math.min(holdout.assistant.engagedVisitors, holdout.holdout.engagedVisitors);
  const progress = holdout.readyAt ? Math.min(smaller / holdout.readyAt, 1) : 0;

  return (
    <section
      aria-label="Randomised holdout"
      className="mt-6 pt-5 border-t dark:border-border-color border-gray-200"
    >
      <div className="flex items-baseline justify-between gap-3 flex-wrap mb-1">
        <h3 className="text-sm font-medium dark:text-text-primary text-gray-900">
          Measured lift (randomised holdout)
        </h3>
        {holdout.ready && holdout.lift !== null && (
          <span className="text-lg font-semibold tabular-nums text-emerald-600 dark:text-emerald-400">
            {holdout.lift > 0 ? '+' : '−'}
            {share(Math.abs(holdout.lift))}
          </span>
        )}
      </div>

      <p className="text-xs dark:text-text-secondary text-gray-500 mb-3">
        Two randomly assigned halves of the same shoppers. Unlike the comparison above, this
        difference is caused by the assistant.
      </p>

      <div className="grid grid-cols-2 gap-3 mb-3">
        <Tile
          label="Saw the assistant"
          value={share(holdout.assistant.conversionRate)}
          note={`${count(holdout.assistant.orders)} orders from ${count(
            holdout.assistant.engagedVisitors
          )} shoppers`}
        />
        <Tile
          label="Held back"
          value={share(holdout.holdout.conversionRate)}
          note={`${count(holdout.holdout.orders)} orders from ${count(
            holdout.holdout.engagedVisitors
          )} shoppers`}
        />
      </div>

      {!holdout.ready && holdout.readyAt && (
        <div>
          <div className="flex items-baseline justify-between gap-3 text-xs dark:text-text-secondary text-gray-500 mb-1">
            <span>Accumulating sample before a lift can be stated</span>
            <span className="tabular-nums">
              {count(smaller)} / {count(holdout.readyAt)} per group
            </span>
          </div>
          <div className="h-2 dark:bg-background/60 bg-gray-100 rounded-full overflow-hidden">
            <div
              className="h-full rounded-full transition-all"
              style={{ width: `${Math.max(progress * 100, 1.5)}%`, background: 'var(--c-assisted)' }}
              role="img"
              aria-label={`${Math.round(progress * 100)}% of the sample needed`}
            />
          </div>
        </div>
      )}
    </section>
  );
}

function ReturnOnSpendTile({
  data,
  money
}: {
  data: ValueScorecardData;
  money: (value: number) => string;
}) {
  const { returnOnSpend } = data;

  if (!returnOnSpend.marginConfigured) {
    return (
      <Tile
        label="Return on spend"
        value="—"
        note={`Add your gross margin to compare against the ${money(returnOnSpend.cost)} spent`}
      />
    );
  }

  if (returnOnSpend.multiple === null) {
    return <Tile label="Return on spend" value="—" note="Nothing spent in this period" />;
  }

  return (
    <Tile
      label="Return on spend"
      value={`${returnOnSpend.multiple.toFixed(1)}×`}
      note={`${money(returnOnSpend.grossProfit ?? 0)} gross profit on ${money(returnOnSpend.cost)}`}
      emphasis
    />
  );
}

function ReachRow({
  data,
  share,
  count
}: {
  data: ValueScorecardData;
  share: (value: number) => string;
  count: (value: number) => string;
}) {
  const { reach } = data;
  const unmeasured = reach.visitors === 0;

  return (
    <section
      aria-label="Assistant reach"
      className="mt-3 grid grid-cols-2 lg:grid-cols-4 gap-3"
    >
      <Tile
        label="Visitors shown the assistant"
        value={unmeasured ? '—' : count(reach.visitors)}
      />
      <Tile
        label="Started a conversation"
        value={unmeasured ? '—' : count(reach.engagedVisitors)}
      />
      <Tile
        label="Engagement rate"
        value={unmeasured ? '—' : share(reach.engagementRate)}
        note={unmeasured ? 'Waiting for the storefront to report visits' : undefined}
      />
      <Tile
        label="Assisted conversion"
        value={reach.assistedConversionRate === null ? '—' : share(reach.assistedConversionRate)}
        note={reach.assistedConversionRate === null ? 'Not measured yet' : 'Of shoppers who engaged'}
      />
    </section>
  );
}

function Tile({
  label,
  value,
  note,
  emphasis = false
}: {
  label: string;
  value: string;
  note?: string;
  emphasis?: boolean;
}) {
  return (
    <div className="dark:bg-background/40 bg-gray-50 rounded-lg p-3">
      <p className="text-xs dark:text-text-secondary text-gray-500">{label}</p>
      <p
        className={`mt-1 tabular-nums font-semibold dark:text-text-primary text-gray-900 ${
          emphasis ? 'text-2xl' : 'text-xl'
        }`}
      >
        {value}
      </p>
      {note && <p className="mt-0.5 text-xs dark:text-text-secondary text-gray-500">{note}</p>}
    </div>
  );
}
